/**
 * OverviewTab — the new default landing view for the doctor's Landing
 * Page Editor.
 *
 * The purpose is to answer "is my public page working for me?" at a
 * glance. Rather than dropping the doctor into an editing surface on
 * arrival, we surface:
 *   1. A hero card with the live URL, Preview + Share buttons, and a
 *      simple "Live / Needs setup" status pill.
 *   2. A grid of health checks (Profile · Trust badges · Content ·
 *      Contact) — each card carries a ✓/!/× indicator and a one-tap
 *      Fix link that jumps to the correct tab.
 *   3. A "Content pipeline" section that surfaces drafts + items
 *      awaiting Invisique's review, so nothing rots unnoticed.
 *   4. A "Next best actions" preview of the top-3 unclaimed local-SEO
 *      topics, with Draft-this links that jump straight into the
 *      full-page editor.
 *
 * No new backend endpoints — the tab composes from existing:
 *   • state (from parent's GET /doctor/landing-page/edit)
 *   • GET /doctor/content/{kind}          × 3
 *   • GET /doctor/content/suggested-topics
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ArrowRight, ArrowSquareOut, CheckCircle, Copy, Eye,
    ImageSquare, Lightbulb, MagicWand, Newspaper, PencilSimple,
    ShareNetwork, Sparkle, Warning, XCircle, ChatCircleText,
    CircleNotch, PaperPlaneTilt, MapPin, Phone, WhatsappLogo,
    Envelope, SealCheck, UserCircle,
} from "@phosphor-icons/react";


/* iter100.46e — Accent palette mirrored from DoctorLandingPage /
   PublicArticlePage / ContentEditorPage so the Overview tab respects
   the doctor's chosen brand colour (teal / rose / amber / slate)
   instead of falling back to the app's default primary. */
const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;


const STATUS = {
    ok:     { icon: CheckCircle, cls: "text-emerald-600", chipCls: "bg-emerald-50 text-emerald-700 border-emerald-200",  label: "Ready" },
    warn:   { icon: Warning,     cls: "text-amber-600",   chipCls: "bg-amber-50 text-amber-800 border-amber-200",         label: "Add detail" },
    bad:    { icon: XCircle,     cls: "text-red-600",     chipCls: "bg-red-50 text-red-700 border-red-200",               label: "Missing" },
};


export default function OverviewTab({ state, publicPath, onNavigateTab }) {
    const [content, setContent] = useState({ loading: true, articles: [], case_studies: [], testimonials: [] });
    const [topics, setTopics] = useState({ loading: true, list: [], city: "" });
    const accent = pickAccent(state?.accent);

    useEffect(() => {
        (async () => {
            try {
                const [a, c, t, sug] = await Promise.all([
                    api.get("/doctor/content/article").catch(() => ({ data: [] })),
                    api.get("/doctor/content/case_study").catch(() => ({ data: [] })),
                    api.get("/doctor/content/testimonial").catch(() => ({ data: [] })),
                    api.get("/doctor/content/suggested-topics").catch(() => ({ data: { topics: [], city: "" } })),
                ]);
                setContent({
                    loading: false,
                    articles: Array.isArray(a.data) ? a.data : [],
                    case_studies: Array.isArray(c.data) ? c.data : [],
                    testimonials: Array.isArray(t.data) ? t.data : [],
                });
                setTopics({
                    loading: false,
                    list: sug.data?.topics || [],
                    city: sug.data?.city || "",
                });
            } catch {
                setContent((s) => ({ ...s, loading: false }));
                setTopics((s) => ({ ...s, loading: false }));
            }
        })();
    }, []);

    /* ------------------------------------------------------------------
       Derived indicators. Each health card gets a `.status` of ok / warn
       / bad plus a short reason string. The parent state carries the
       doctor's design-tab fields; content counts come from our own
       fetch above.
       ------------------------------------------------------------------ */
    const doctorName = state?.doctor_name || "";
    const hasPhoto = Boolean(state?.hero?.image);
    // iter100.276 — Detect the legacy auto-generated boilerplate story
    // ("Certified Invisique provider at {clinic}.") — that string was
    // injected by the old admin-manual-create flow before we slimmed
    // the create form. It looks like a real story but is a template —
    // flag it so the Profile health card nudges the doctor to write
    // their own before publishing.
    const rawStory = (state?.story || "").trim();
    const isPlaceholderStory = /^certified invisique(™)? provider at\b/i.test(rawStory);
    const hasStory = rawStory.length > 40 && !isPlaceholderStory;
    const hasCity = Boolean(state?.hero?.city);
    const hasClinic = Boolean(state?.clinic_name);
    const autoChipCount = Object.values(state?.chip_config?.auto_visibility || {}).filter(Boolean).length;
    const adminChipCount = (state?.chip_config?.admin_badges || []).length;
    const doctorChipCount = Object.values(state?.chip_config?.doctor_flags || {}).filter(Boolean).length;
    const totalChipCount = autoChipCount + adminChipCount + doctorChipCount;

    const publishedArticles = content.articles.filter((x) => (x.status || "published") === "published");
    const draftArticles = content.articles.filter((x) => x.status === "draft");
    const publishedCases = content.case_studies.filter((x) => (x.status || "published") === "published");
    const pendingCases = content.case_studies.filter((x) => x.status === "pending_review");
    const draftCases = content.case_studies.filter((x) => x.status === "draft");
    const needsChangesCases = content.case_studies.filter((x) => x.status === "needs_changes" || x.status === "rejected");
    const publishedTestimonials = content.testimonials.filter((x) => (x.status || "published") === "published");

    /* Profile card: photo + name + story + city + clinic → all ok = green;
       missing one detail = amber; missing 2+ = red. */
    const profileMissing = [
        !hasPhoto && "photo",
        !hasStory && (isPlaceholderStory ? "story (placeholder)" : "story"),
        !hasCity && "city",
        !hasClinic && "clinic",
    ].filter(Boolean);
    const profileStatus = !doctorName ? "bad" : profileMissing.length === 0 ? "ok" : profileMissing.length === 1 ? "warn" : "bad";

    const trustStatus = totalChipCount >= 3 ? "ok" : totalChipCount >= 1 ? "warn" : "bad";

    const contentTotal = publishedArticles.length + publishedCases.length + publishedTestimonials.length;
    const contentStatus = contentTotal >= 5 ? "ok" : contentTotal >= 1 ? "warn" : "bad";

    const hasPhone = Boolean(state?.cta_phone || state?.profile_phone);
    const hasWhatsapp = Boolean(state?.cta_whatsapp || state?.profile_whatsapp);
    const contactChannels = [hasPhone, hasWhatsapp].filter(Boolean).length;
    const contactStatus = contactChannels >= 2 ? "ok" : contactChannels >= 1 ? "warn" : "bad";

    /* Live/Setup pill for the hero card */
    const publicIsReady = profileStatus === "ok" && contentTotal > 0 && contactChannels >= 1;

    /* ------------------------------------------------------------------
       Copy-to-clipboard helper for the URL card.
       ------------------------------------------------------------------ */
    const copyUrl = async () => {
        try {
            const origin = typeof window !== "undefined" ? window.location.origin : "";
            await navigator.clipboard.writeText(`${origin}${publicPath || ""}`);
            toast.success("URL copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    const displayUrl = publicPath ? `${(typeof window !== "undefined" ? window.location.host : "")}${publicPath}` : "—";

    return (
        <div className="space-y-6" data-testid="landing-overview-tab">
            {/* iter100.279 — "Your public landing page" hero card removed
                per user direction. URL + Live/Needs-setup pill + Preview
                button now live on the identity hero at the very top of
                the editor (see EditorIdentityHero in LandingPageEditor).
                Removing the duplicate here keeps the Overview tab focused
                on the health cards + content pipeline + suggested topics. */}

            {/* ═══ HEALTH CARDS ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="overview-health-grid">
                <HealthCard
                    testid="overview-health-profile"
                    icon={UserCircle}
                    label="Profile"
                    status={profileStatus}
                    accent={accent}
                    subtitle={
                        profileStatus === "ok" ? "Photo, story, city, clinic — all set."
                        : profileMissing.length ? `Missing ${profileMissing.join(", ")}.`
                        : "Add your name to get started."
                    }
                    onFix={() => onNavigateTab("design", "landing-story")}
                    fixLabel="Edit profile"
                />
                <HealthCard
                    testid="overview-health-trust"
                    icon={SealCheck}
                    label="Trust badges"
                    status={trustStatus}
                    accent={accent}
                    subtitle={
                        totalChipCount === 0 ? "No trust chips yet."
                        : totalChipCount === 1 ? "1 chip active — aim for 3+."
                        : `${totalChipCount} chips active${autoChipCount ? ` (${autoChipCount} auto)` : ""}.`
                    }
                    onFix={() => onNavigateTab("design", "landing-chips")}
                    fixLabel="Manage chips"
                />
                <HealthCard
                    testid="overview-health-content"
                    icon={Newspaper}
                    label="Content"
                    status={contentStatus}
                    accent={accent}
                    subtitle={
                        contentTotal === 0 ? "No published content yet."
                        : `${publishedArticles.length} article${publishedArticles.length === 1 ? "" : "s"} · ${publishedCases.length} case${publishedCases.length === 1 ? "" : "s"} · ${publishedTestimonials.length} review${publishedTestimonials.length === 1 ? "" : "s"}.`
                    }
                    onFix={() => onNavigateTab(publishedArticles.length === 0 ? "topics" : "article")}
                    fixLabel={publishedArticles.length === 0 ? "See topic ideas" : "Add more"}
                />
                <HealthCard
                    testid="overview-health-contact"
                    icon={Phone}
                    label="Contact"
                    status={contactStatus}
                    accent={accent}
                    subtitle={
                        contactChannels === 0 ? "No booking phone or WhatsApp set."
                        : contactChannels === 1 ? "1 channel — add WhatsApp for faster leads."
                        : "Phone + WhatsApp both available."
                    }
                    onFix={() => onNavigateTab("design", "landing-contact")}
                    fixLabel="Add contact"
                />
            </div>

            {/* ═══ 3. CONTENT PIPELINE ═══ */}
            {(pendingCases.length + draftArticles.length + draftCases.length + needsChangesCases.length) > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4" data-testid="overview-pipeline">
                    <div className="flex items-center gap-2">
                        <PencilSimple size={16} weight="duotone" className="text-primary" />
                        <h3 className="font-display text-base font-bold">Your content pipeline</h3>
                    </div>

                    {/* Pending Invisique™ review */}
                    {pendingCases.length > 0 && (
                        <PipelineGroup
                            heading={`In review (${pendingCases.length})`}
                            headingIcon={CircleNotch}
                            headingIconCls="text-amber-500 animate-spin"
                            subheading="Waiting on Invisique™ — usually approved within 24 hours."
                        >
                            {pendingCases.slice(0, 3).map((it) => (
                                <PipelineRow
                                    key={it.id}
                                    icon={Sparkle}
                                    title={it.data?.title || "Untitled case"}
                                    subtitle="Case study · pending review"
                                    editHref={`/doctor/content/case_study/${it.id}/edit`}
                                />
                            ))}
                        </PipelineGroup>
                    )}

                    {/* Needs changes / rejected */}
                    {needsChangesCases.length > 0 && (
                        <PipelineGroup
                            heading={`Needs your attention (${needsChangesCases.length})`}
                            headingIcon={Warning}
                            headingIconCls="text-red-500"
                            subheading="Reviewer left feedback — open to see and update."
                        >
                            {needsChangesCases.slice(0, 3).map((it) => (
                                <PipelineRow
                                    key={it.id}
                                    icon={Sparkle}
                                    title={it.data?.title || "Untitled case"}
                                    subtitle="Case study · needs changes"
                                    editHref={`/doctor/content/case_study/${it.id}/edit`}
                                />
                            ))}
                        </PipelineGroup>
                    )}

                    {/* Drafts */}
                    {(draftArticles.length + draftCases.length) > 0 && (
                        <PipelineGroup
                            heading={`Drafts (${draftArticles.length + draftCases.length})`}
                            headingIcon={PencilSimple}
                            headingIconCls="text-slate-500"
                            subheading="Only you can see these — publish when ready."
                        >
                            {[...draftArticles, ...draftCases].slice(0, 4).map((it) => (
                                <PipelineRow
                                    key={it.id}
                                    icon={draftArticles.includes(it) ? Newspaper : Sparkle}
                                    title={it.data?.title || "Untitled draft"}
                                    subtitle={draftArticles.includes(it) ? "Article · draft" : "Case study · draft"}
                                    editHref={`/doctor/content/${draftArticles.includes(it) ? "article" : "case_study"}/${it.id}/edit`}
                                />
                            ))}
                        </PipelineGroup>
                    )}
                </div>
            )}

            {/* ═══ 4. NEXT-BEST-ACTIONS (SEO topics) ═══ */}
            {!topics.loading && topics.list.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-5 sm:p-6" data-testid="overview-topics-preview">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                            <Lightbulb size={20} weight="fill" className="text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-display text-base font-bold flex items-center gap-2 flex-wrap">
                                Suggested next articles
                                {topics.city && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md border bg-white border-amber-200 text-amber-800">
                                        <MapPin size={9} weight="fill" /> {topics.city}
                                    </span>
                                )}
                            </h3>
                            <p className="text-[12.5px] text-slate-600 mt-0.5">
                                High-demand local SEO topics you haven&apos;t written yet. Draft one — it&apos;s already outlined for you.
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0 text-[12px]"
                            onClick={() => onNavigateTab("topics")}
                            data-testid="overview-topics-see-all"
                        >
                            See all {topics.list.length} <ArrowRight size={12} weight="bold" />
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mt-4">
                        {topics.list.slice(0, 3).map((t) => (
                            <div
                                key={t.id}
                                className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col hover:border-slate-300 hover:shadow-sm transition-all"
                                data-testid={`overview-topic-${t.id}`}
                            >
                                <div className="font-display text-[13.5px] font-bold text-slate-900 leading-snug line-clamp-3 mb-2">
                                    {t.title}
                                </div>
                                <div className="mt-auto">
                                    <Button asChild size="sm" className="h-7 rounded-full text-[11.5px] w-full">
                                        <Link to={`/doctor/content/article/new?from_topic=${encodeURIComponent(t.id)}`}>
                                            <MagicWand size={11} weight="fill" /> Draft this
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ═══ 5. Empty-state help card if content is very light ═══ */}
            {contentTotal === 0 && !content.loading && (
                <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-center" data-testid="overview-empty-help">
                    <ImageSquare size={28} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
                    <div className="font-display text-base font-bold text-slate-900">Get your page ready in ~10 minutes</div>
                    <p className="text-[13px] text-slate-500 mt-1 max-w-md mx-auto">
                        Add a photo and short story on <b>Design & Story</b>, draft one topic-seeded article, and confirm a
                        contact channel. That&apos;s enough to start converting traffic.
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                        <Button size="sm" onClick={() => onNavigateTab("design")}>
                            <PencilSimple size={13} /> Start with Design
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onNavigateTab("topics")}>
                            <Lightbulb size={13} /> Browse topic ideas
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}


/* ==========================================================================
   Small building blocks — kept local so this file is one drop-in import.
   ========================================================================== */

function HealthCard({ icon: Icon, label, status, subtitle, onFix, fixLabel, testid, accent }) {
    const meta = STATUS[status] || STATUS.warn;
    const StatusIcon = meta.icon;
    return (
        <div className="rounded-2xl border border-border bg-card p-4 flex flex-col hover:shadow-sm transition-shadow" data-testid={testid}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent?.soft || "hsl(215 60% 97%)" }}
                >
                    <Icon size={16} weight="duotone" style={{ color: accent?.text || "hsl(215 60% 25%)" }} />
                </div>
                <span
                    className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${meta.chipCls}`}
                    data-testid={`${testid}-status`}
                >
                    <StatusIcon size={9} weight="fill" /> {meta.label}
                </span>
            </div>
            <div className="font-semibold text-[13.5px] text-slate-900 leading-tight">{label}</div>
            <p className="text-[11.5px] text-slate-500 mt-1 leading-snug min-h-[2.5em]">{subtitle}</p>
            {status !== "ok" && (
                <button
                    type="button"
                    onClick={onFix}
                    className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold hover:gap-1.5 transition-all w-fit"
                    style={{ color: accent?.text || "hsl(215 60% 25%)" }}
                    data-testid={`${testid}-fix`}
                >
                    {fixLabel || "Fix"} <ArrowRight size={11} weight="bold" />
                </button>
            )}
        </div>
    );
}


function PipelineGroup({ heading, headingIcon: HeadingIcon, headingIconCls, subheading, children }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-1">
                {HeadingIcon && <HeadingIcon size={12} weight="fill" className={headingIconCls} />}
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-700">{heading}</div>
            </div>
            {subheading && <div className="text-[11.5px] text-slate-500 mb-2">{subheading}</div>}
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

function PipelineRow({ icon: Icon, title, subtitle, editHref }) {
    return (
        <Link
            to={editHref}
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5 hover:border-slate-300 hover:shadow-sm transition-all"
        >
            <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <Icon size={14} weight="duotone" className="text-slate-700" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900 truncate">{title}</div>
                <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>
            </div>
            <ArrowRight size={12} weight="bold" className="text-slate-400" />
        </Link>
    );
}
